import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  WrenchScrewdriverIcon,
  ClipboardDocumentCheckIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

// Inicio del operario: su día de un vistazo — el trabajo del taller filtrado
// por SU área (solo lectura: los estados los maneja el admin desde Taller y
// acá se reflejan solos) y los checklists de diagnóstico abiertos, que son
// transversales y los ve todo el mundo.
// (El piloto WES de "Siguiente tarea" se durmió: sin terminales en el taller
// no se usa, y auto-asignaba tareas viejas a quien abriera esta pantalla.)

interface QueueItem {
  id: string;
  appointmentCode: string;
  status: string;
  client?: { firstName?: string; lastName?: string; companyName?: string };
  vehicle?: { licensePlate?: string | null; make?: string | null; model?: string | null };
  stationQueue?: string[];
  stationsCompleted?: string[];
  currentStation?: string | null;
  services?: Array<{ name?: string; quantity?: number }>;
  serviceType?: string;
}

interface OpenDiagnosis {
  id: string;
  diagnosisNumber: string;
  clientName?: string;
  vehiclePlate?: string;
  status: string;
  createdAt: string;
}

const STATUS_LABEL: Record<string, string> = {
  checked_in: 'En espera',
  in_progress: 'En proceso',
  paused: 'Pausado',
  waiting_parts: 'Esperando repuestos',
  ready_for_pickup: 'Listo para retirar'
};

const normalizar = (s?: string | null) =>
  String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export default function OperatorHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const [stationNames, setStationNames] = useState<Record<string, string>>({});
  const [diagnostics, setDiagnostics] = useState<OpenDiagnosis[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [queueRes, diagRes] = await Promise.allSettled([
        axios.get('/api/reception/queue'),
        axios.get('/api/diagnostics')
      ]);
      if (queueRes.status === 'fulfilled') {
        const byStation = queueRes.value.data.queue || {};
        const items: QueueItem[] = Object.values(byStation).flat() as QueueItem[];
        const ready: QueueItem[] = queueRes.value.data.readyForPickup || [];
        setQueue([...items, ...ready]);
        const names: Record<string, string> = {};
        (queueRes.value.data.stations || []).forEach((s: any) => { names[s.key] = s.name; });
        setStationNames(names);
      }
      if (diagRes.status === 'fulfilled') {
        const all = diagRes.value.data.diagnostics || [];
        setDiagnostics(all.filter((d: OpenDiagnosis) => d.status === 'open').slice(0, 5));
      }
      setLoading(false);
    })();
  }, []);

  // El área del usuario (Configuración → Usuarios) se corresponde por nombre
  // con la estación del taller: "Gomería" → estación 'gomeria'. Sin área
  // asignada (o un área sin estación, ej. Herramientas) se ve todo.
  const miEstacion = useMemo(() => {
    const area = normalizar((user as any)?.areaName);
    if (!area) return null;
    return Object.keys(stationNames).find((key) => normalizar(key) === area || normalizar(stationNames[key]) === area) || null;
  }, [user, stationNames]);

  // Estaciones que a esta OT todavía le faltan pasar
  const estacionesPendientes = (it: QueueItem) => {
    const done = new Set(it.stationsCompleted || []);
    return (it.stationQueue || []).filter((k) => !done.has(k));
  };

  const esDeMiArea = (it: QueueItem) => {
    if (!miEstacion) return true;
    const pendientes = estacionesPendientes(it);
    if (pendientes.length === 0) return true; // OT sin estaciones: la ven todos
    return pendientes.includes(miEstacion);
  };

  // La lista de trabajo: lo que está en el taller y toca a mi área.
  // "Listo para retirar" no es trabajo pendiente: queda en el contador nomás.
  const trabajo = useMemo(
    () => queue.filter((q) => q.status !== 'ready_for_pickup' && esDeMiArea(q)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queue, miEstacion]
  );

  const counts = useMemo(() => ({
    enProceso: trabajo.filter(q => q.status === 'in_progress').length,
    enEspera: trabajo.filter(q => q.status === 'checked_in').length,
    listos: queue.filter(q => q.status === 'ready_for_pickup').length
  }), [trabajo, queue]);

  const clientName = (c?: QueueItem['client']) =>
    c?.companyName || `${c?.firstName || ''} ${c?.lastName || ''}`.trim() || 'Sin cliente';

  const firstName = `${(user as any)?.firstName || ''}`.trim();

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Cargando tu día...</div>;
  }

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 p-4 md:p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{firstName ? `Hola, ${firstName}` : 'Tu día'}</h1>
        <p className="text-sm text-muted-foreground">
          {new Date().toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {/* Trabajo en el taller: las OTs abiertas de mi área, con sus servicios.
          Solo lectura: completa/pausa el admin y acá se refleja. */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <WrenchScrewdriverIcon className="h-5 w-5" /> Trabajo en el taller
            {miEstacion && (
              <Badge variant="secondary" className="text-xs font-normal">
                {stationNames[miEstacion] || miEstacion}
              </Badge>
            )}
          </CardTitle>
          <Button size="sm" className="h-8" onClick={() => navigate('/taller')}>
            Ir al taller <ArrowRightIcon className="ml-1 h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-xl border px-3 py-2">
              <p className="text-xl font-bold">{counts.enProceso}</p>
              <p className="text-xs text-muted-foreground">En proceso</p>
            </div>
            <div className="rounded-xl border px-3 py-2">
              <p className="text-xl font-bold">{counts.enEspera}</p>
              <p className="text-xs text-muted-foreground">En espera</p>
            </div>
            <div className="rounded-xl border px-3 py-2">
              <p className="text-xl font-bold">{counts.listos}</p>
              <p className="text-xs text-muted-foreground">Para retirar</p>
            </div>
          </div>

          {trabajo.length > 0 && (
            <div className="mt-3 space-y-2">
              {trabajo.slice(0, 6).map(it => {
                const pendientes = estacionesPendientes(it);
                const servicios = (it.services || []).filter(s => s?.name);
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => navigate('/taller')}
                    className="w-full rounded-lg border px-3 py-2 text-left text-sm hover:bg-accent"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold">{it.appointmentCode}</span>
                      <span className="min-w-0 flex-1 truncate text-muted-foreground">
                        {clientName(it.client)}{it.vehicle?.licensePlate ? ` · ${it.vehicle.licensePlate}` : ''}
                      </span>
                      <Badge variant="outline" className="shrink-0 text-xs">
                        {STATUS_LABEL[it.status] || it.status}
                      </Badge>
                    </div>
                    {servicios.length > 0 && (
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        {servicios.map(s => `${s.name}${Number(s.quantity) > 1 ? ` x${s.quantity}` : ''}`).join(' · ')}
                      </p>
                    )}
                    {pendientes.length > 1 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {pendientes.map(k => (
                          <span
                            key={k}
                            className={`rounded px-1.5 py-0.5 text-[10px] ${k === miEstacion ? 'bg-primary/15 text-primary font-medium' : 'bg-muted text-muted-foreground'}`}
                          >
                            {stationNames[k] || k}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {trabajo.length === 0 && (
            <p className="mt-3 text-sm text-muted-foreground">
              {miEstacion ? `Sin trabajo pendiente para ${stationNames[miEstacion] || 'tu área'} ahora.` : 'No hay autos en el taller ahora.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Checklists de diagnóstico abiertos: transversales, los ven todos */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardDocumentCheckIcon className="h-5 w-5" /> Checklists abiertos
          </CardTitle>
          <Button size="sm" variant="outline" className="h-8" onClick={() => navigate('/diagnostics')}>
            Ver todos
          </Button>
        </CardHeader>
        <CardContent className="pt-0">
          {diagnostics.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay checklists pendientes de completar.</p>
          ) : (
            <div className="space-y-1.5">
              {diagnostics.map(d => (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => navigate(`/diagnostics/${d.id}/checklist`)}
                  className="flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm hover:bg-accent"
                >
                  <span className="font-mono font-semibold">{d.diagnosisNumber}</span>
                  <span className="min-w-0 flex-1 truncate text-muted-foreground">
                    {d.clientName || 'Sin cliente'}{d.vehiclePlate ? ` · ${d.vehiclePlate}` : ''}
                  </span>
                  <ArrowRightIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
